import { Injectable } from '@angular/core';
import { OfferResponse } from '@dr-customer-offers-ui/lib-interfaces';
import { Prices } from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import { exportFile } from 'common-utils/dist/csv-export';
import moment from 'moment';
import { NgxDropdownOption } from 'ngx-dropdown';
import { ExportReportOutput, TabOptionsEnum, TabOptionValue } from '../models';

interface ExportOffersModel {
  registrationId: string;
  registrationName: string;
  date: string;
  intervalStart: string;
  intervalEnd: string;
  offerValue?: string;
  capacityIncrease?: string;
  capacityDecrease?: string;
  optOut?: string;
  price?: Prices;
  capacity?: string;
  capacity_volume?: string;
  offerPrice?: string;
  demandForecast?: string;
  demand?: string;
  priceIncrease?: string;
  priceDecrease?: string;
}

@Injectable({ providedIn: 'root' })
export class ExportCSVService {
  protected headerTitlesForTabs = {
    default: Object.freeze({
      registrationId: 'COM.CSV.CSV_REG_ID',
      registrationName: 'COM.CSV.CSV_REG_NAME',
      date: 'COM.CSV.CSV_DATE_RANGE',
      intervalStart: 'COM.CSV.CSV_INTERVAL_START',
      intervalEnd: 'COM.CSV.CSV_INTERVAL_END',
      offerValue: 'COM.CSV.CSV_OFFER_VALUE',
      capacityIncrease: 'COM.CSV.CSV_KW_TO_INCREASE',
      capacityDecrease: 'COM.CSV.CSV_KW_TO_DECREASE',
      priceIncrease: 'COM.CSV.CSV_PRICE_TO_INCREASE',
      priceDecrease: 'COM.CSV.CSV_PRICE_TO_DECREASE',
      optOut: 'COM.CSV.CSV_OPT_OUT',
      offerPrice: 'COM.CSV.CSV_OFFER_PRICE',
      demandForecast: 'COM.CSV.CSV_DEMAND_FORECAST'
    }),
    clear: Object.freeze({
      registrationId: 'COM.CSV.CSV_REG_ID',
      registrationName: 'COM.CSV.CSV_REG_NAME',
      date: 'COM.CSV.CSV_DATE_RANGE',
      intervalStart: 'COM.CSV.CSV_INTERVAL_START',
      intervalEnd: 'COM.CSV.CSV_INTERVAL_END',
      capacity_volume: 'COM.CSV.CSV_CAPACITY_VOLUME',
      capacity_bonus: 'COM.CSV.CAPACITY_BONUS_PRICE',
      capacity: 'COM.CSV.CSV_CAPACITY_PRICE',
      energy: 'COM.CSV.CSV_ENERGY_PRICE'
    })
  };

  // Row 2: stable, untranslated keys used to identify columns on upload
  protected readonly csvKeysForDefaultTab: Readonly<Record<string, string>> = Object.freeze({
    registrationId: 'registration_id',
    registrationName: 'registration_name',
    date: 'date',
    intervalStart: 'interval_start',
    intervalEnd: 'interval_end',
    offerValue: 'kW',
    capacityIncrease: 'KW_TO_INCREASE',
    capacityDecrease: 'KW_TO_DECREASE',
    priceIncrease: 'PRICE_TO_INCREASE',
    priceDecrease: 'PRICE_TO_DECREASE',
    optOut: 'opt_out',
    offerPrice: 'offer_price',
    demandForecast: 'DEMAND_FORECAST'
  });

  constructor(private translate: TranslateService) {}

  public exportCSV(
    dateRange: ExportReportOutput,
    timeZone: string | undefined,
    selectedRegistration: NgxDropdownOption,
    offer: OfferResponse[] | null | undefined,
    tabName: TabOptionValue,
    offerDimensions: string[] = [],
    isConsolidatedToHourly = false
  ): void {
    if (!offer || !timeZone) return;

    const hasKw = offerDimensions.includes('kW');
    const hasSplitCapacity = offerDimensions.includes('KW_TO_INCREASE') && offerDimensions.includes('KW_TO_DECREASE');
    const hasSplitPrice = offerDimensions.includes('PRICE_TO_INCREASE') && offerDimensions.includes('PRICE_TO_DECREASE');

    const filterStart = moment.tz(dateRange.startMomentDate?.format('YYYY-MM-DD HH:mm:ss'), timeZone);
    const filterEnd = moment.tz(dateRange.endMomentDate?.format('YYYY-MM-DD HH:mm:ss'), timeZone);

    const filteredData: OfferResponse[] = offer.filter((elem: OfferResponse) => {
      const start = moment.tz(elem.offer_start_dttm_utc, timeZone);
      const end = moment.tz(elem.offer_end_dttm_utc, timeZone);
      return end.isSameOrBefore(filterEnd) && start.isSameOrAfter(filterStart);
    });

    const sortedData: OfferResponse[] = filteredData.sort((a, b) => moment(a.offer_start_dttm_utc).diff(moment(b.offer_start_dttm_utc)));

    if (!sortedData.length || !selectedRegistration) return;

    const formatValue = (value: any): string => (value == null ? '' : value.toString());

    let dataForCSV: ExportOffersModel[];

    if (tabName === TabOptionsEnum.Cleared) {
      // ── Cleared tab — unchanged behaviour ────────────────────────────────
      dataForCSV = sortedData.map((elem: OfferResponse) => ({
        registrationId: selectedRegistration.id,
        registrationName: `"${selectedRegistration.value}"`,
        date: moment(elem.offer_start_dttm_utc).tz(timeZone).format('"ddd, DD MMMM YYYY"'),
        intervalStart: moment(elem.offer_start_dttm_utc).tz(timeZone).format('H:mm'),
        intervalEnd: moment(elem.offer_end_dttm_utc).tz(timeZone).format('H:mm'),
        capacity_volume: formatValue(elem.cleared?.quantities?.['capacity']),
        capacity_bonus: formatValue(elem.cleared?.prices?.['capacity_bonus']),
        capacity: formatValue(elem.cleared?.prices?.['capacity']),
        energy: formatValue(elem.cleared?.prices?.['energy'])
      }));
    } else {
      // ── Open / Schedule / Baseline tabs ──────────────────────────────────

      const hasPriceInExport: boolean = offer.some((o: OfferResponse) => 'price' in o.offer_value);

      dataForCSV = sortedData.map((elem: OfferResponse) => {
        const row: ExportOffersModel = {
          registrationId: selectedRegistration.id,
          registrationName: `"${selectedRegistration.value}"`,
          date: moment(elem.offer_start_dttm_utc).tz(timeZone).format('"ddd, DD MMMM YYYY"'),
          intervalStart: moment(elem.offer_start_dttm_utc).tz(timeZone).format('H:mm'),
          intervalEnd: moment(elem.offer_end_dttm_utc).tz(timeZone).format('H:mm'),
          demandForecast: formatValue(elem.offer_value['DEMAND_FORECAST'])
        };

        // Opt-out is not applicable to the consolidated/Polish market — omit the column entirely
        if (!isConsolidatedToHourly) {
          row.optOut = elem.offer_opt_out_state ? this.translate.instant('COM.CSV.CSV_YES') : this.translate.instant('COM.CSV.CSV_NO');
        }

        // Capacity: single kW column, OR split increase/decrease — per offer_dimensions
        if (hasKw) {
          row.offerValue = formatValue(elem.offer_value['kW']);
        } else if (hasSplitCapacity) {
          row.capacityIncrease = formatValue(elem.offer_value['KW_TO_INCREASE']);
          row.capacityDecrease = formatValue(elem.offer_value['KW_TO_DECREASE']);
        }

        // Only include the price field when the registration supports price.
        // Using key: lowercase 'price' matches the API's offer_value dictionary.
        if (hasSplitPrice) {
          row.priceIncrease = formatValue(elem.offer_value['PRICE_TO_INCREASE']);
          row.priceDecrease = formatValue(elem.offer_value['PRICE_TO_DECREASE']);
        } else if (hasPriceInExport) {
          row.offerPrice = formatValue(elem.offer_value['price']);
        }

        return row;
      });
    }

    exportFile(this.buildDataForCSV(dataForCSV, tabName), this.buildUniqueFileName());
  }

  private buildDataForCSV(dataForCSV: ExportOffersModel[], tabName: TabOptionValue): string {
    const isCleared = tabName === TabOptionsEnum.Cleared;
    const headerMap = this.headerTitlesForTabs[isCleared ? 'clear' : 'default'] as Readonly<Record<string, string>>;

    const presentKeys = new Set<string>();
    dataForCSV.forEach((row) => Object.keys(row).forEach((k) => presentKeys.add(k)));
    const columnKeys = Object.keys(headerMap).filter((key) => presentKeys.has(key));

    // Row 1: translated labels (escaped — translations may contain Hyphen)
    const labelRow = columnKeys.map((key) => this.sanitizeCsvCell(this.translate.instant(headerMap[key]))).join(',');

    const csvArray: string[] = [labelRow];

    // Row 2: identification keys (upload template tabs only)
    if (!isCleared) {
      csvArray.push(columnKeys.map((key) => this.csvKeysForDefaultTab[key] ?? key).join(','));
    }

    // Row 3+: data
    csvArray.push(...dataForCSV.map((row) => columnKeys.map((key) => (row as any)[key] ?? '').join(',')));

    return csvArray.join('\n');
  }

  private sanitizeCsvCell(value: unknown): string {
    const s = value == null ? '' : String(value);
    return s.replace(/[",\r\n]+/g, '-');
  }

  private buildUniqueFileName(): string {
    const timestamp: number = Date.now();
    const randomId: string = Math.random().toString(36).substring(7);
    const fileName = `csv_export_offers_${timestamp}_${randomId}`;
    return fileName;
  }
}
